import React, { useCallback, useMemo, useRef, useState } from "react";
import { connect } from "dvajs";
import ComNoticeCard from "@/baseComponents/ComNoticeCard";
import { changeEgovUrl } from "@/utils/tools";
import { message as toast } from "antd";
import DeeplinkEvent from "@/components/DeeplinkHandler/DeeplinkEvent";
import { FocusSDK } from "@/utils";
import { genThirdAppUrl } from "@/components/ThirdApp";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import imgDefault from "@/components/DefaultPage/imgs/img-load-failed.png";
import { addXtoken } from "@/utils/addToken";
import IconFont from "@/components/icon";

function ImageTextCard({ message }: { message: any }) {
  const { authInfo, appRuntimeEnv } = React.useContext<GlobalContextConfig>(GlobalContext);
  const { title, extend = {}, sender, infox = {} } = message;
  const isPrivateNet = useRef<boolean>(FocusSDK.isPrivateNet());
  const [showMore, setShowMore] = useState<boolean>(false);
  const avatarSrc = sender.avatar;

  const { bannerImageUrl, bannerImageEgovUrl, bannerTitle, imageFields, bannerPcUrl = "" } =
    extend || {};

  const handleClickLink = useCallback(
    (url: string) => {
      if (!url) {
        return;
      }
      const parseUrl = DeeplinkEvent.parseDeeplink(url);
      if (!parseUrl) {
        toast.warn("当前跳转链接不符合规范");
        return;
      }
      if (parseUrl.pathname && parseUrl.pathname.includes("browser")) {
        const mparam = parseUrl.mparam || {};
        let realUrl = isPrivateNet.current ? mparam.egovUrl : mparam.url;
        if (!realUrl) {
          toast.warn("该应用不支持在当前网络环境下打开");
          return;
        }
        const applicationKey = extend?.applicationKey || infox?.applicationKey;
        if (applicationKey) {
          realUrl = genThirdAppUrl(
            applicationKey,
            authInfo.ddAppId,
            authInfo.accessToken,
            appRuntimeEnv,
            {
              redirectUrl: realUrl
            }
          );
        }
        window.open(realUrl);
      } else {
        DeeplinkEvent.open(url);
      }
    },
    [
      appRuntimeEnv,
      authInfo?.accessToken,
      authInfo?.ddAppId,
      extend?.applicationKey,
      infox?.applicationKey,
      message
    ]
  );

  const getLinkClass = useCallback((link: string) => {
    if (link) {
      return "cursor-pointer";
    } else {
      return "";
    }
  }, []);

  const imgOnError = (e: any) => {
    try {
      if (e.target) {
        e.target.src = imgDefault;
        e.target.className = "error";
      }
    } catch(e) {}
  };

  const getRealImageUrl = useCallback((govUrl: string, egovUrl: string) => {
    const imgUrl = isPrivateNet.current ? egovUrl || govUrl : govUrl;
    return addXtoken(imgUrl);
  }, []);

  const hasMainImage = useMemo(() => {
    return bannerImageUrl || bannerImageEgovUrl || bannerTitle;
  }, [bannerImageEgovUrl, bannerImageUrl, bannerTitle]);

  // 主图
  const mainImage = useMemo(() => {
    return hasMainImage ? (
      <div
        className={`main-wrap ${getLinkClass(bannerPcUrl)}`}
        onClick={() => handleClickLink(bannerPcUrl)}
      >
        {(bannerImageUrl || bannerImageEgovUrl) && (
          <div className="img-wrap">
            <img src={getRealImageUrl(bannerImageUrl, bannerImageEgovUrl)} onError={imgOnError} />
          </div>
        )}
        {bannerTitle && <span className="main-title three-lines">{bannerTitle}</span>}
      </div>
    ) : null;
  }, [
    bannerImageEgovUrl,
    bannerPcUrl,
    bannerImageUrl,
    bannerTitle,
    getLinkClass,
    getRealImageUrl,
    handleClickLink,
    hasMainImage
  ]);

  const hasMore = useMemo(() => {
    return (imageFields?.length || 0) > 3;
  }, [imageFields?.length]);

  const showImageList = useMemo(() => {
    if (!hasMore) {
      return imageFields;
    } else if (!showMore) {
      return (imageFields || []).slice(0, 3);
    } else {
      return imageFields;
    }
  }, [hasMore, imageFields, showMore]);

  // 图文列表
  const imageList = useMemo(() => {
    if (!imageFields || !imageFields.length) {
      return null;
    }
    return (
      <div className={`info-list-wrap ${showMore ? "more" : ""}`}>
        <div className="content">
          {showImageList.map((item: any, idx: number) => {
            const nolink = !item?.linkName || !item?.pcUrl;
            return (
              <div
                className={`info-item ${getLinkClass(item?.pcUrl)} ${
                  item?.imageUrl || item?.imageEgovUrl ? "" : "no-img"
                }`}
                key={`${message.id}_img_${idx}`}
                onClick={() => handleClickLink(item?.pcUrl)}
              >
                {item?.imageUrl && (
                  <div className="img-wrap">
                    <img
                      src={getRealImageUrl(item?.imageUrl, item?.imageEgovUrl)}
                      onError={imgOnError}
                    />
                  </div>
                )}
                <div className="info-content">
                  <span
                    className={`text ${!nolink ? "two-lines" : "all-line"}`}
                    dangerouslySetInnerHTML={{ __html: item?.content }}
                  />
                  {!nolink && <span className="link ellipsis">{item?.linkName}</span>}
                </div>
              </div>
            );
          })}
        </div>
        {hasMore && !showMore ? (
          <div className="footer" onClick={() => setShowMore(true)}>
            <span className="text">{`余下${imageFields?.length - 3}篇`}</span>
            <IconFont className="icon" type="iconicon_down" />
          </div>
        ) : null}
      </div>
    );
  }, [
    getLinkClass,
    getRealImageUrl,
    handleClickLink,
    hasMore,
    imageFields,
    message.id,
    showImageList,
    showMore
  ]);

  return (
    <ComNoticeCard
      message={message}
      avatarSrc={changeEgovUrl(avatarSrc)}
      type={extend?.applicationName}
      title={title}
    >
      <div className="imgcard-content-wrap">
        {mainImage}
        {imageList}
      </div>
    </ComNoticeCard>
  );
}

function mapStateToProps() {
  return {};
}

function mapDispatchToProps(dispatch: any) {
  return {};
}

export default connect(mapStateToProps, mapDispatchToProps)(ImageTextCard);
